import React from "react";
import "../../signin/tutorSignin.css";
import {
  Col,
  Row,
  Typography,
  Form,
  Layout,
  Input,
  Button,
  Image,
} from "antd";
import { useNavigate } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { Post } from "../../../config/api/post";
import { ASSOCIATE_AUTH } from "../../../config/constants/api";
import { addUser } from "../../../redux/slice/authSlice";
import { FiMail, FiLock, FiBookOpen, FiPlayCircle, FiFolder } from "react-icons/fi";
import swal from "sweetalert";
import { ImageUrl } from "../../../helpers/Functions";

function AssociateSignin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.userData);
  const token = useSelector((state) => state.user.userToken);
  const userRole = useSelector((state) => state.user.userRole);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (user && token && userRole === "ASSOCIATE") {
      navigate("/associate/dashboard", { replace: true });
    } else if (user && token && userRole === "COACH") {
      navigate("/dashboard", { replace: true });
    }
  }, [user, token, userRole, navigate]);

  const onFinish = (values) => {
    setLoading(true);
    Post(ASSOCIATE_AUTH.signin, {
      email: values.email,
      password: values.password,
    })
      .then((response) => {
        setLoading(false);
        if (response?.data?.status) {
          const { associate, coach, token: authToken } = response.data.data;
          dispatch(
            addUser({
              user: { ...associate, coach },
              token: authToken,
              role: "ASSOCIATE",
            })
          );
          swal("Success", response.data.message, "success");
          navigate("/associate/dashboard", { replace: true });
        } else {
          swal("Oops!", response?.data?.message || "Login failed", "error");
        }
      })
      .catch(() => {
        setLoading(false);
        swal("Oops!", "Unable to sign in", "error");
      });
  };

  return (
    <Layout className="AuthBackground tutorSigninLayout" style={{ minHeight: "100vh" }}>
      <Row className="tutorSigninRow" gutter={[0, 0]}>
        <Col xs={24} lg={12} className="tutorSigninFormCol">
          <div className="tutorSigninFormShell">
            <div className="tutorSigninFormCard">
              <div className="tutorSigninCardGlow" aria-hidden />
              <Image
                src={ImageUrl("fav.png")}
                className="tutorSigninLogo"
                alt="WrightCo Academy"
                preview={false}
              />
              <Typography.Title level={2} className="fontFamily1 tutorSigninTitle">
                Associate portal
              </Typography.Title>
              <Typography.Paragraph className="tutorSigninSubtitle">
                Sign in to browse courses, lectures, and resources assigned by
                your trainer.
              </Typography.Paragraph>

              <div className="tutorSigninPills" aria-hidden>
                <span className="tutorSigninPill">Courses</span>
                <span className="tutorSigninPill">Lectures</span>
                <span className="tutorSigninPill">Resources</span>
              </div>

              <Form
                layout="vertical"
                className="loginForm tutorSigninForm"
                onFinish={onFinish}
                autoComplete="off"
              >
                <Form.Item
                  label="Email Address*"
                  name="email"
                  rules={[
                    { type: "email", message: "Please enter a valid email" },
                    { required: true, message: "Email is required" },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="Enter email"
                    className="tutorSigninInput"
                    prefix={<FiMail />}
                  />
                </Form.Item>
                <Form.Item
                  label="Password*"
                  name="password"
                  rules={[{ required: true, message: "Password is required" }]}
                >
                  <Input.Password
                    size="large"
                    prefix={<FiLock />}
                    placeholder="Enter password"
                    className="tutorSigninInput"
                  />
                </Form.Item>
                <div className="tutorSigninForgotRow">
                  <button
                    type="button"
                    className="tutorSigninForgotButton"
                    onClick={() => navigate("/associate/forgot-password")}
                  >
                    Forgot Password?
                  </button>
                </div>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="loginButton tutorSigninSubmit"
                    block
                  >
                    {loading ? "Loading..." : "Login as Associate"}
                  </Button>
                </Form.Item>
              </Form>

              <Typography.Text className="tutorSigninFooterText fontFamily1">
                Trainer account?{" "}
                <span
                  className="tutorSigninRegisterLink"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate("/signin")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      navigate("/signin");
                    }
                  }}
                >
                  Login as Trainer
                </span>
              </Typography.Text>
            </div>
          </div>
        </Col>
        <Col xs={0} lg={12} className="tutorSigninPromoCol">
          <div className="tutorSigninPromoBg" aria-hidden />
          <div className="tutorSigninPromoContent">
            <Image
              src={ImageUrl("fav.png")}
              className="tutorSigninPromoLogo"
              alt="WrightCo Academy"
              preview={false}
            />
            <Typography.Title level={2} className="tutorSigninPromoTitle">
              Your assigned learning content
            </Typography.Title>
            <Typography.Paragraph className="tutorSigninPromoSubtitle">
              Access only the courses your trainer has granted you — including
              lectures and shared resources.
            </Typography.Paragraph>

            <div className="tutorSigninFeatureGrid">
              <div className="tutorSigninFeature">
                <div className="tutorSigninFeatureIcon">
                  <FiBookOpen />
                </div>
                <span className="tutorSigninFeatureTitle">Courses</span>
                <span className="tutorSigninFeatureText">
                  View every course your trainer assigned to you.
                </span>
              </div>
              <div className="tutorSigninFeature">
                <div className="tutorSigninFeatureIcon">
                  <FiPlayCircle />
                </div>
                <span className="tutorSigninFeatureTitle">Lectures</span>
                <span className="tutorSigninFeatureText">
                  Watch lesson videos and review materials.
                </span>
              </div>
              <div className="tutorSigninFeature">
                <div className="tutorSigninFeatureIcon">
                  <FiFolder />
                </div>
                <span className="tutorSigninFeatureTitle">Resources</span>
                <span className="tutorSigninFeatureText">
                  Open PDFs, images, and files linked to each course.
                </span>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Layout>
  );
}

export default AssociateSignin;
